/* eslint-disable no-continue */
import {
  call,
  take,
  select,
  race,
  takeLatest,
  fork,
  cancel,
  put,
} from 'redux-saga/effects';
import moment from 'moment-timezone';

import { login } from '../auth/actions';
import { fetchDevicesStats } from '../devices/actions';
import { changeCurrentDevice } from '../../components/Header/actions';
import {
  changeCurrentJourney,
  previewJourney,
} from '../../components/Sidebar/RidesSidebar/actions';
import {
  queryJourneysForRides,
  getLocations,
  getEventsGeoData,
  checkJourneysEverExist,
  checkJourneysForMonthExist,
  appealEventPenalty,
} from './actions';
import { filterRides } from '../../components/Sidebar/actions';
import { bindAsyncAction } from '../../utils/sagas';
import { apiRequest, geoCodeRequest } from '../../utils/api';
import { appealEventClicked } from '../../components/Rides/actions';
import { getEventType } from '../../utils/helpers';

const eventsGeoDataWorker = bindAsyncAction(getEventsGeoData, function* load(
  params,
) {
  return yield call(geoCodeRequest, params);
});

const geoCodeWorker = bindAsyncAction(getLocations, function* load(params) {
  return yield call(geoCodeRequest, params);
});

const appealEventWorker = bindAsyncAction(appealEventPenalty, function* load(
  params,
) {
  return yield call(apiRequest, params);
});
const worker = bindAsyncAction(queryJourneysForRides, function* load(params) {
  const { data, cursorInfo, pagingInfo } = yield call(apiRequest, params);
  let geoResponseStarts;
  let geoResponseFinishes;
  if (!data.length) return { data, cursorInfo, pagingInfo };
  try {
    geoResponseStarts = yield call(geoCodeWorker, {
      body: JSON.stringify({
        requests: data.map(item => ({
          Latitude: item.journey.stat.startLat / 10000000,
          Longitude: item.journey.stat.startLon / 10000000,
        })),
      }),
    });
    geoResponseFinishes = yield call(geoCodeWorker, {
      body: JSON.stringify({
        requests: data.map(item => ({
          Latitude: item.journey.stat.finishLat / 10000000,
          Longitude: item.journey.stat.finishLon / 10000000,
        })),
      }),
    });
  } catch (e) {
    geoResponseStarts = [];
    return;
  }

  const returnData = data.map((j, index) => ({
    ...j,
    ...{
      geoData: {
        start: geoResponseStarts[index],
        finish: geoResponseFinishes[index],
      },
    },
  }));

  return {
    data: returnData,
    cursorInfo,
    pagingInfo,
  };
});

function* journeysEverExistSaga() {
  while (true) {
    yield put(checkJourneysEverExist.started({}));
    const deviceId = yield select(state => state.devices.currentDevice.id);
    const params = {
      Sort: '-starttime',
      From: moment()
        .subtract(10, 'year')
        .startOf('day')
        .utc()
        .format(),
      UpTo: moment()
        .endOf('day')
        .utc()
        .format(),
      DurationFrom: 0 * 60,
      DurationUpTo: 720 * 60,
      DistanceFrom: 0 * 1000,
      DistanceUpTo: 1000 * 1000,
      Take: 10,
      Skip: 0,
      Devices: [deviceId],
    };
    try {
      const { pagingInfo } = yield call(apiRequest, {
        url: '/api/v3/Journeys',
        params: {
          ...params,
          ExcludeEvents: true,
        },
      });
      yield put(checkJourneysEverExist.done({ pagingInfo }));
    } catch (e) {
      continue;
    }
    yield take(changeCurrentDevice.type);
  }
}

function* journeysForMonthExistSaga() {
  while (true) {
    yield put(checkJourneysForMonthExist.started({}));
    const deviceId = yield select(state => state.devices.currentDevice.id);
    const params = {
      Sort: '-starttime',
      From: moment()
        .subtract(1, 'month')
        .startOf('day')
        .utc()
        .format(),
      UpTo: moment()
        .endOf('day')
        .utc()
        .format(),
      DurationFrom: 0 * 60,
      DurationUpTo: 720 * 60,
      DistanceFrom: 0 * 1000,
      DistanceUpTo: 1000 * 1000,
      Take: 10,
      Skip: 0,
      Devices: [deviceId],
    };
    try {
      const { pagingInfo } = yield call(apiRequest, {
        url: '/api/v3/Journeys',
        params: {
          ...params,
          ExcludeEvents: true,
        },
      });
      yield put(checkJourneysForMonthExist.done({ pagingInfo }));
    } catch (e) {
      continue;
    }
    yield take(changeCurrentDevice.type);
  }
}

function* filterJourneysSaga(action) {
  const params = action.payload;
  const deviceId = yield select(state => state.devices.currentDevice.id);

  try {
    yield call(worker, {
      url: '/api/v3/Journeys',
      params: {
        Devices: [deviceId],
        Sort: `${params.sortDirection ? '' : '-'}${params.sortBy}`,
        From: params.range[0].startOf('day').format(),
        UpTo: params.range[1].endOf('day').format(),
        DurationFrom: params.duration[0] * 60,
        DurationUpTo: params.duration[1] * 60,
        DistanceFrom: params.distance[0] * 1000,
        DistanceUpTo: params.distance[1] * 1000,
        Take: 10,
        Skip: params.page * 10,
        ExcludeEvents: true,
      },
    });
  } catch (e) {}
}

function* getEventsGeoDataSaga() {
  while (true) {
    const { payload } = yield take([
      changeCurrentJourney.type,
      previewJourney.type,
    ]);
    if (payload.journey === null) continue;

    const events = payload.journey.journey.events.filter(e => e.penalties);
    if (!events.length) continue;
    const geoRequestData = events.map(e => ({
      Latitude: e.lat / 10000000,
      Longitude: e.lon / 10000000,
    }));
    const params = {
      body: JSON.stringify({ requests: geoRequestData }),
    };
    try {
      yield call(eventsGeoDataWorker, params);
    } catch (e) {
      return;
    }
  }
}

function* appealEventSaga() {
  while (true) {
    const { payload } = yield take(appealEventClicked.type);
    const eventContainer = yield select(state => state.rides.appealingEvent);
    const appeal = {
      eventId: eventContainer.event.id,
      journeyId: eventContainer.journey.id,
      comment: payload.comment,
      type: getEventType(eventContainer.event.penalties),
    };
    try {
      yield call(appealEventWorker, {
        url: '/api/v3/Appeal',
        method: 'POST',
        body: JSON.stringify(appeal),
      });
    } catch (e) {
      console.log(e);
    }
  }
}

export default function* ridesSaga() {
  yield take(login.done.type);
  yield take(fetchDevicesStats.done.type);
  yield fork(journeysEverExistSaga);
  yield fork(journeysForMonthExistSaga);
  yield fork(appealEventSaga);

  const devices = yield select(state => state.devices.devices);
  if (devices.length) {
    while (true) {
      const url = yield select(
        state => state.routing.locationBeforeTransitions.pathname,
      );
      const deviceId = yield select(state => state.devices.currentDevice.id);
      const filterParams = yield select(state => state.rides.filter.params) ||
        {};
      const params = {
        Sort: '-starttime',
        From: moment()
          .subtract(1, 'month')
          .startOf('day')
          .utc()
          .format(),
        UpTo: moment()
          .endOf('day')
          .utc()
          .format(),
        DurationFrom: 0 * 60,
        DurationUpTo: 720 * 60,
        DistanceFrom: 0 * 1000,
        DistanceUpTo: 1000 * 1000,
        Take: 10,
        Skip: 0,
        ...filterParams,
        Devices: [deviceId],
      };
      try {
        yield call(worker, {
          url: '/api/v3/Journeys',
          params: {
            ...params,
            ExcludeEvents: true,
          },
        });
      } catch (e) {
        return;
      }

      yield fork(getEventsGeoDataSaga);
      const ridesFilterSaga = yield takeLatest(
        filterRides.type,
        filterJourneysSaga,
      );
      while (true) {
        const { changeDevice, locationChange } = yield race({
          changeDevice: take(changeCurrentDevice.type),
          locationChange: take('@@router/LOCATION_CHANGE'),
        });
        if (locationChange && locationChange.payload.pathname !== '/rides') {
          continue;
        } else {
          break;
        }
      }
      yield cancel(ridesFilterSaga);
    }
  }
}



// WEBPACK FOOTER //
// ./src/sagas/rides/index.js